import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  Directive,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { APP_VERSION_GETTER, APP_VERSION_LONG_PRESS_MS } from '@cue/api';

@Directive({
  selector: '[cueAppVersionTrigger]',
  standalone: true,
})
export class AppVersionTriggerDirective implements OnDestroy {
  private applicationRef = inject(ApplicationRef);
  private hostElement = inject(ElementRef<HTMLElement>);
  private environmentInjector = inject(EnvironmentInjector);
  private getVersion = inject(APP_VERSION_GETTER);
  private longPressDurationMs = inject(APP_VERSION_LONG_PRESS_MS);
  private longPressTimeoutId: number | null = null;
  private longPressTriggered = false;
  private popupVisible = signal(false);
  private popupComponentRef: ComponentRef<unknown> | null = null;
  private popupHostElement: HTMLElement | null = null;
  private popupInitPromise: Promise<void> | null = null;

  version = signal<string | null>(null);

  constructor() {
    this.version.set(this.getVersion());
  }

  ngOnDestroy(): void {
    this.clearLongPressTimeout();
    this.destroyPopup();
  }

  private closePopup() {
    this.popupVisible.set(false);
    this.popupComponentRef?.setInput('visible', false);
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    if (event.button !== 0) {
      return;
    }
    this.clearLongPressTimeout();
    this.longPressTriggered = false;
    this.longPressTimeoutId = window.setTimeout(() => {
      this.version.set(this.getVersion());
      this.longPressTriggered = true;
      this.popupVisible.set(true);
      void this.showPopup();
    }, this.longPressDurationMs);
  }

  @HostListener('pointerup')
  @HostListener('pointerleave')
  @HostListener('pointercancel')
  onPointerStop() {
    this.clearLongPressTimeout();
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.longPressTriggered) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.longPressTriggered = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.popupVisible()) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.hostElement.nativeElement.contains(target)) {
      this.closePopup();
    }
  }

  private clearLongPressTimeout() {
    if (this.longPressTimeoutId !== null) {
      clearTimeout(this.longPressTimeoutId);
      this.longPressTimeoutId = null;
    }
  }

  private async showPopup() {
    await this.ensurePopup();
    this.popupComponentRef?.setInput('version', this.version());
    this.popupComponentRef?.setInput('visible', true);
  }

  private async ensurePopup() {
    if (this.popupComponentRef) {
      return;
    }
    if (this.popupInitPromise) {
      await this.popupInitPromise;
      return;
    }

    this.popupInitPromise = (async () => {
      const { AppVersionPopupComponent } = await import('../components/app-version-popup/app-version-popup.component');
      this.popupHostElement = document.createElement('div');
      this.hostElement.nativeElement.appendChild(this.popupHostElement);
      this.popupComponentRef = createComponent(AppVersionPopupComponent, {
        hostElement: this.popupHostElement,
        environmentInjector: this.environmentInjector,
      });
      this.applicationRef.attachView(this.popupComponentRef.hostView);
      this.popupComponentRef.setInput('version', null);
      this.popupComponentRef.setInput('visible', false);
      this.popupComponentRef.changeDetectorRef.detectChanges();
    })();

    await this.popupInitPromise;
    this.popupInitPromise = null;
  }

  private destroyPopup() {
    if (this.popupComponentRef) {
      this.applicationRef.detachView(this.popupComponentRef.hostView);
    }
    this.popupComponentRef?.destroy();
    this.popupComponentRef = null;
    if (this.popupHostElement?.parentNode) {
      this.popupHostElement.parentNode.removeChild(this.popupHostElement);
    }
    this.popupHostElement = null;
    this.popupInitPromise = null;
  }
}
